import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Filter } from 'lucide-react';
import { trainingApi } from '../../services/trainingApi';

export default function TrainingAdminDashboard() {
  const [filtros, setFiltros] = useState<{
    empresa?: string;
    moduloId?: string;
    status?: string;
    from?: string;
    to?: string;
  }>({});

  const statsQ = useQuery({
    queryKey: ['training-admin-stats'],
    queryFn: () => trainingApi.admin.getStats(),
  });

  const modulosQ = useQuery({
    queryKey: ['training-admin-modules'],
    queryFn: () => trainingApi.admin.getModules(),
  });

  const attemptsQ = useQuery({
    queryKey: ['training-admin-attempts', filtros],
    queryFn: () =>
      trainingApi.admin.getAttempts({
        empresa: filtros.empresa || undefined,
        moduloId: filtros.moduloId || undefined,
        status: filtros.status || undefined,
        from: filtros.from || undefined,
        to: filtros.to || undefined,
        limit: 100,
      }),
  });

  const [downloading, setDownloading] = useState(false);
  async function handleDownload() {
    setDownloading(true);
    try {
      const blob = await trainingApi.admin.downloadExcel({
        empresa: filtros.empresa,
        moduloId: filtros.moduloId,
        status: filtros.status,
        from: filtros.from,
        to: filtros.to,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capacitacion-intentos-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const s = statsQ.data;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Capacitación · Dashboard</h1>
        <p className="text-gray-500 text-sm">
          Panel de control del módulo de capacitación.
        </p>
      </div>

      {statsQ.isLoading && <p className="text-gray-500">Cargando estadísticas...</p>}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Usuarios" value={s.total_usuarios} />
          <StatCard label="Intentos" value={s.total_intentos} />
          <StatCard label="Aprobados" value={s.aprobados} tone="green" />
          <StatCard label="Reprobados" value={s.reprobados} tone="red" />
          <StatCard label="% aprobación" value={`${s.porcentaje_aprobacion}%`} tone="cyan" />
        </div>
      )}

      {s?.por_modulo?.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Por módulo</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2">Módulo</th>
                  <th className="px-4 py-2">Intentos</th>
                  <th className="px-4 py-2">Aprobados</th>
                  <th className="px-4 py-2">Reprobados</th>
                  <th className="px-4 py-2">% aprobación</th>
                </tr>
              </thead>
              <tbody>
                {s.por_modulo.map((m: any) => (
                  <tr key={m.modulo_id} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-2">{m.modulo_nombre}</td>
                    <td className="px-4 py-2">{m.intentos}</td>
                    <td className="px-4 py-2 text-green-600">{m.aprobados}</td>
                    <td className="px-4 py-2 text-red-600">{m.reprobados}</td>
                    <td className="px-4 py-2">{m.porcentaje_aprobacion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wider text-gray-500 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Intentos
          </h2>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-2 text-sm rounded-md bg-blue-600 text-white px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Generando...' : 'Exportar Excel'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <input
            type="text"
            placeholder="Empresa"
            value={filtros.empresa ?? ''}
            onChange={(e) => setFiltros({ ...filtros, empresa: e.target.value })}
            className="text-sm rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 px-3 py-2"
          />
          <select
            value={filtros.moduloId ?? ''}
            onChange={(e) => setFiltros({ ...filtros, moduloId: e.target.value })}
            className="text-sm rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 px-3 py-2"
          >
            <option value="">Todos los módulos</option>
            {modulosQ.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
          <select
            value={filtros.status ?? ''}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
            className="text-sm rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 px-3 py-2"
          >
            <option value="">Cualquier estado</option>
            <option value="passed">Aprobado</option>
            <option value="failed">Reprobado</option>
            <option value="in_progress">En progreso</option>
          </select>
          <input
            type="date"
            value={filtros.from ?? ''}
            onChange={(e) => setFiltros({ ...filtros, from: e.target.value })}
            className="text-sm rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 px-3 py-2"
          />
          <input
            type="date"
            value={filtros.to ?? ''}
            onChange={(e) => setFiltros({ ...filtros, to: e.target.value })}
            className="text-sm rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 px-3 py-2"
          />
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2">Usuario</th>
                <th className="px-4 py-2">Empresa</th>
                <th className="px-4 py-2">Módulo</th>
                <th className="px-4 py-2">Puntaje</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Iniciado</th>
                <th className="px-4 py-2">Duración</th>
              </tr>
            </thead>
            <tbody>
              {attemptsQ.data?.data.map((a: any) => (
                <tr key={a.id} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-4 py-2">
                    {a.usuario_nombre}
                    <span className="block text-xs text-gray-500">{a.usuario_email}</span>
                  </td>
                  <td className="px-4 py-2">{a.usuario_empresa}</td>
                  <td className="px-4 py-2">{a.modulo_nombre}</td>
                  <td className="px-4 py-2 font-mono">{a.puntaje}%</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {a.iniciado_at ? new Date(a.iniciado_at).toLocaleString() : ''}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {a.duracion_seg ? `${a.duracion_seg}s` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: 'green' | 'red' | 'cyan';
}) {
  const color =
    tone === 'green'
      ? 'text-green-600'
      : tone === 'red'
        ? 'text-red-600'
        : tone === 'cyan'
          ? 'text-cyan-600'
          : 'text-gray-800 dark:text-white';
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tones: Record<string, string> = {
    passed: 'bg-green-100 text-green-700 border-green-300',
    failed: 'bg-red-100 text-red-700 border-red-300',
    in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  };
  const labels: Record<string, string> = {
    passed: 'Aprobado',
    failed: 'Reprobado',
    in_progress: 'En progreso',
  };
  return (
    <span className={`inline-flex text-xs px-2 py-0.5 rounded-full border ${tones[status] ?? ''}`}>
      {labels[status] ?? status}
    </span>
  );
}
